import { Suspense, useEffect } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { ThemeProvider } from "styled-components";
import { useAppSelector } from '../common/store';
import { themeSelector } from '../common/store/theme/selectors';
import { appStateSelector } from '../common/store/app-state/selectors';
import { GlobalStyles } from './global-styles';
import { WidgetButtonSkeleton } from './modules/chat-button/components/widget-button-skeleton';
import { ChatMessagesSkeleton } from './modules/chat-messages/components/chat-messages-skeleton';
import { ChatContextProvider } from './contexts/chat-context';
import { ChatFooterSkeleton } from './modules/chat-footer/components/chat-footer-skeleton';
import isPropValid from '@emotion/is-prop-valid'
import { ChatFrameSkeleton } from './modules/chat-frame/components/chat-frame-skeleton';
import { NudgeSkeleton } from './modules/nudge/components/nudge-skeleton';
import { chatSelector } from '../common/store/chat/selectors';

import './index.css';
import { BookAppointment } from './modules/book-appointment';

import WidgetButton from './modules/chat-button';
import ChatFrame from './modules/chat-frame';
import ChatHeader from './modules/chat-header';
import ChatMessages from './modules/chat-messages';
import ChatFooter from './modules/chat-footer';
import Nudge from './modules/nudge';
import useInitApp from './hooks/use-init-app';
import ChatActions from './modules/chat-actions';

const buildForExtension = process.env.BUILD_FOR_EXTENSION
let ChatCall: React.ComponentType;
if (buildForExtension) {
  ChatCall = require('./modules/chat-call').default;
} else {
  const { lazyWithPreload } = require('react-lazy-with-preload');
  ChatCall = lazyWithPreload(() => import('./modules/chat-call'));
}

export const App = () => {
  const storeTheme = useAppSelector(themeSelector);
  const storeAppState = useAppSelector(appStateSelector);
  const storeChat = useAppSelector(chatSelector);
  const isOnCall = useAppSelector(state => state.calls.isOnCall);
  useEffect(() => {
    if (!buildForExtension && storeAppState.callEnabled) {
      (ChatCall as any).preload?.();
    }
  }, [storeAppState.callEnabled]);

  useInitApp();

  return (
    <StyleSheetManager shouldForwardProp={isPropValid}>
      <ThemeProvider theme={storeTheme}>
        <GlobalStyles />
        <ChatContextProvider>
          {storeAppState.shouldDisplay && (
            <RootContainer className="voa_chat_button_container">
              <Suspense fallback={<ChatFrameSkeleton />}>
                <ChatFrame
                  frameHeaderComponent={
                    <Suspense>
                      <ChatHeader />
                    </Suspense>
                  }
                  frameBodyComponent={
                    <Suspense fallback={<ChatMessagesSkeleton />}>
                      <ChatMessages />
                    </Suspense>
                  }
                  frameFooterComponent={
                    <Suspense fallback={<ChatFooterSkeleton />}>
                      <ChatFooter />
                    </Suspense>
                  }
                  frameActionsComponent={
                    <ChatActions />
                  }
                />
              </Suspense>

              {isOnCall && (
                buildForExtension ? (
                  <ChatCall />
                ) : (
                  <Suspense fallback={<div>Loading call features...</div>}>
                    <ChatCall />
                  </Suspense>
                )
              )}

              <NudgeContainer isOpen={storeAppState.isNudgeOpen && !storeAppState.isOpen && !!storeChat.messages.length}>
                <Suspense fallback={<NudgeSkeleton />}>
                  <Nudge />
                </Suspense>
              </NudgeContainer>

              <Suspense fallback={<WidgetButtonSkeleton />}>
                <WidgetButton />
              </Suspense>
            </RootContainer>
          )}
          <BookAppointment />
        </ChatContextProvider>
      </ThemeProvider>
    </StyleSheetManager>
  )
};

const NudgeContainer = styled.div<{ isOpen: boolean }>`
  display: ${(props) => props.isOpen ? 'block' : 'none'};
`;

const RootContainer = styled.div`
  all: initial;
  position: fixed;
  bottom: 5px;
  right: 5px;
  font-family: 'Poppins', sans-serif;
  z-index: 9999;

  @media (min-width: 768px) {
    bottom: 40px;
    right: 40px;
  }
`;